import type { Document } from '../api/authoring'

// A document's *derived* fields, read the way every other consumer reads
// them.
//
// `derived` is what the component's type computes from its own fields and
// the deployment the platform is running as — for a component that holds
// a file, where its bytes are and where each rendition's are. It is not
// stored, it is not sendable, and it is deliberately a separate member
// from `fields` so that a form prefilled by name cannot pick one up and
// restate it as something the author claimed.
//
// **Nothing here computes a URL.** The whole reason `component.Derive`
// exists is that the alternative — publishing the origin, the key scheme
// and the derivative naming, and letting each consumer join them — is
// three facts the platform could then never change. This client is a
// consumer like any other, so it reads what the document says and joins
// nothing.
//
// Which type this applies to is never assumed: a client asks the manifest
// whether a type `acceptsUpload`, and a component whose type does is one
// that may have these. A second type that holds a file gets the same
// treatment the day it is registered.

/** One rendition the platform generated, as the document reports it. */
export interface Rendition {
  width: number
  height: number
  url: string
}

/**
 * Where the component's own bytes are, if anywhere.
 *
 * **Optional, and the absence has two ordinary causes** a client must not
 * distinguish because a consumer cannot act on the difference: nothing
 * has been uploaded yet, or this stage names no origin to resolve a key
 * under. Either way an internal component composes with no url at all,
 * which is honest, because there is nothing a browser could fetch.
 *
 * Both deployed stages now name one — the blob CDN in front of the
 * private bucket — and so does the local loop, where it is the object
 * store itself. That makes the second cause rare rather than gone, and
 * rare is the dangerous kind: a stage stood up without the origin
 * configured composes exactly like a library of files nobody uploaded,
 * so this stays optional and every caller keeps testing presence.
 */
export function fileUrl(document: Document | undefined): string | undefined {
  const url = document?.derived?.url
  return typeof url === 'string' && url !== '' ? url : undefined
}

/**
 * The renditions that exist, smallest first.
 *
 * **Which ones exist is a fact the document carries, never a rule.** The
 * platform skips a width larger than the original — upscaling a thumbnail
 * manufactures detail that was never in the file — and makes none at all
 * for a document, so a client computing the set from a naming scheme
 * would name renditions nobody generated and render broken images for
 * every small logo in the library.
 */
export function renditions(document: Document | undefined): Rendition[] {
  const raw = document?.derived?.derivatives
  if (!Array.isArray(raw)) return []
  return raw
    .filter(
      (entry): entry is Rendition =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as Rendition).url === 'string' &&
        typeof (entry as Rendition).width === 'number',
    )
    .slice()
    .sort((left, right) => left.width - right.width)
}

/**
 * The smallest rendition that still fills a box of this width, or the
 * original when none does.
 *
 * The fallback is not a degradation. An image already smaller than every
 * generated width has no renditions *because it needs none*, and a
 * document — a PDF — has none because there is nothing to resize; in both
 * cases the object itself is the only thing there is to show, and in the
 * second there is nothing to show at all, which is why this may still
 * come back undefined.
 */
export function thumbnail(document: Document | undefined, atLeast: number): string | undefined {
  const available = renditions(document)
  const chosen = available.find((rendition) => rendition.width >= atLeast) ?? available.at(-1)
  return chosen?.url ?? fileUrl(document)
}

/**
 * Whether the component's bytes are an image a browser will render.
 *
 * Read off the stored content type, which is a *reading* the platform
 * took from the object rather than anything a caller said — so a PDF
 * uploaded as `image/png` is still reported as a PDF here.
 */
export function isImage(document: Document | undefined): boolean {
  const fields = document?.fields as Record<string, unknown> | undefined
  const contentType = fields?.contentType
  return typeof contentType === 'string' && contentType.startsWith('image/')
}
